import {Component, EventEmitter, Injector, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FrontendFormComponent} from '@abstract/frontend.form.component';
import {LaboratoryDocumentsNamespace} from '@components/laboratory-documents/laboratory-documents.namespace';
import DocumentTypeEnum = LaboratoryDocumentsNamespace.DocumentTypeEnum;
import {
    LaboratoryDocumentsTypesProvider,
    LaboratoryDocumentsTypesSelectValueInterface
} from '@providers/laboratory-documents-types/laboratory-documents-types.provider';
import {FormHelperService} from '@services/form-helper.service';
import {distinctUntilChanged, filter, map, takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {DateService} from '@services/date.service';
import {LaboratoryDocument} from '@models/laboratory-document';
import {LaboratoryDocumentsProvider} from '@providers/laboratory-documents/laboratory-documents.provider';
import {DocumentApiService} from '@components/laboratory-documents/services/document.api.service';
import {NotyMessagesEnum} from '@helpers/noty-messages.enum';


interface GroupConfig {
    /**
     * Подпись поля с номером
     */
    numberLabel?: string;
    /**
     * Номер документа обязательно к заполнению
     */
    numberRequired?: boolean;
    /**
     * Дата начала обязательна к заполнению
     */
    dateIssueRequired?: boolean;
    /**
     * Окончание обязательно к заполнению
     */
    dateEndRequired?: boolean;
    /**
     * Показывать контрол с датой окончания
     */
    showDateEndControl?: boolean;
    /**
     * Поле организации обязательно к заполнению
     */
    organizationRequired?: boolean;
    /**
     * Подпись поля с организацией, ответственной за документ
     */
    organizationLabel?: string;
    /**
     * Показывать поле ввода собственного имени документа
     */
    showCustomNameField?: boolean;
}

interface ConfigStorage {
    [key: string]: GroupConfig;
}

@Component({
    selector: 'app-laboratory-document-form',
    templateUrl: 'document-form.component.html',
    providers: []
})

export class DocumentFormComponent extends FrontendFormComponent implements OnDestroy {

    @Input() document: LaboratoryDocument;
    @Input() defaultType: LaboratoryDocumentsNamespace.DocumentTypeEnum;

    /**
     * Куда добавляем документы?
     * В Организацию? В Лабораторию? Добавляем все документы?
     */
    @Input() target: 'all' | 'lab' | 'org' = 'all';

    @Output() saved = new EventEmitter();
    @Output() cancelled = new EventEmitter();

    /**
     * Текущая конфигурация формы в зависимости от типа документа
     */
    public config: GroupConfig;

    public documentTypesSelectValues: LaboratoryDocumentsTypesSelectValueInterface[] = [];

    /**
     * Стандартные настройки состояния
     */
    private defaultConfig: GroupConfig = {
        numberLabel: 'Номер документа',
        numberRequired: true,
        dateEndRequired: true,
        dateIssueRequired: true,
        showDateEndControl: true,
        organizationRequired: false,
        organizationLabel: 'Кем выдан документ',
        showCustomNameField: false,
    };

    /**
     * Изменения в стандартных настройках в зависимости от типа документа
     */
    private configStorage: ConfigStorage = {
        [DocumentTypeEnum.attestation]: {
            numberLabel: 'Номер свидетельства',
        },
        [DocumentTypeEnum.iiiLicense]: {
            numberLabel: 'Номер лицензии',
            showDateEndControl: false,
            organizationLabel: 'Орган санитарного надзора, осуществляющий надзор за лабораторией',
            organizationRequired: true,
        },
        [DocumentTypeEnum.sanitation]: {
            numberLabel: 'Номер заключения',
            organizationRequired: true,
        },
        [DocumentTypeEnum.rostehLicense]: {
            numberLabel: 'Номер лицензии',
            organizationRequired: true,
        },
        [DocumentTypeEnum.custom]: {
            numberRequired: false,
            dateIssueRequired: false,
            dateEndRequired: false,
            showCustomNameField: true,
        },
    };

    private destroyed$ = new Subject();

    constructor(
        injector: Injector,
        public formService: DocumentApiService,
        public documentTypesProvider: LaboratoryDocumentsTypesProvider,
        public documentsProvider: LaboratoryDocumentsProvider,
        private dateService: DateService,
    ) {
        super(injector);
    }

    init() {
        this.form = this._fb.group({
            id: [''],
            type: [''],
            custom_name: [''],
            number: [''],
            date_issue: [''],
            date_end: [''],
            issued_by: [''],
            files: this._fb.array([])
        });

        if (this.defaultType) {
            this.form.get('type').patchValue(this.defaultType);
            this.form.get('type').disable();
        }

        if (this.document) {
            this.setFormValues(this.document.rawData, this.form);
            this.form.get('type').disable();
        }

        this.handleTypeChange(this.form.get('type').value);

        this.form.get('date_issue').valueChanges.pipe(
            takeUntil(this.destroyed$),
            distinctUntilChanged(),
            /**
             * Если дата окончания не пуста - ничего не делаем
             */
            filter(() => !Boolean(this.form.get('date_end').value)),
            /**
             * Если дата начала не валидна - ничего не делаем
             */
            filter((dateString) => Boolean(this.dateService.for(dateString).isValid())),
            map((dateString) => {
                const type = +this.form.get('type').value;
                if (type === LaboratoryDocumentsNamespace.DocumentTypeEnum.attestation) {
                    return this.dateService.for(dateString).addMonth(36).dateString;
                }
                if ([
                    LaboratoryDocumentsNamespace.DocumentTypeEnum.sanitation,
                    LaboratoryDocumentsNamespace.DocumentTypeEnum.rostehLicense,
                ].includes(type)) {
                    return this.dateService.for(dateString).addMonth(60).dateString;
                }
                return '';
            })
        ).subscribe((dateEnd: string) => {
            this.form.get('date_end').patchValue(dateEnd);
        });

        switch (this.target) {
            case 'lab': {
                this.documentTypesSelectValues = this.documentTypesProvider.data
                    .filter(item => item.id === LaboratoryDocumentsNamespace.DocumentTypeEnum.attestation);
                break;
            }
            case 'org': {
                this.documentTypesSelectValues = this.documentTypesProvider.data
                    .filter(item => item.id !== LaboratoryDocumentsNamespace.DocumentTypeEnum.attestation);
                break;
            }
            default: {
                this.documentTypesSelectValues = this.documentTypesProvider.data;
                break;
            }
        }

        if (this.documentTypesSelectValues.length === 1 && !this.defaultType) {
            this.form.get('type').patchValue(this.documentTypesSelectValues[0].id);
        }
    }

    onTypeChange() {
        const typeValue = this.form.get('type').value;
        FormHelperService.reset(this.form);
        this.form.get('type').patchValue(typeValue);
        this.handleTypeChange(typeValue);
    }

    afterSave() {
        this.eventsService.showNoty({
            type: 'success',
            content: NotyMessagesEnum.laboratoryDocumentUpdated
        });
        this.documentsProvider.updateData();
        this.saved.emit();
    }

    ngOnDestroy() {
        this.destroyed$.next();
    }

    private handleTypeChange(type: LaboratoryDocumentsNamespace.DocumentTypeEnum) {
        this.config = Object.assign({}, this.defaultConfig, this.configStorage[type]);
    }
}

